/**
 * BURA Theme - Global JavaScript
 * Sklep z akcesoriami dla psow - Adapted for BlueTruck
 */

// ============================================================================
// Utility Functions
// ============================================================================

const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
};

const formatMoney = (cents, format) => {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.theme.moneyFormat || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision || 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) return 0;

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

const fetchConfig = (type = 'json') => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
};

// ============================================================================
// Mobile Menu
// ============================================================================

class MobileMenu {
  constructor() {
    this.menu = document.querySelector('[data-mobile-menu]');
    if (!this.menu) return;

    this.toggleBtn = document.querySelector('[data-menu-toggle]');
    this.closeBtns = this.menu.querySelectorAll('[data-mobile-menu-close]');
    // expand buttons are handled via event delegation in header.liquid

    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtn?.addEventListener('click', () => this.toggle());

    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  toggle() {
    if (this.menu.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.menu.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.menu.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

// ============================================================================
// Search
// ============================================================================

class Search {
  constructor() {
    this.overlay = document.querySelector('[data-search-overlay]');
    if (!this.overlay) return;

    this.toggleBtns = document.querySelectorAll('[data-search-toggle]');
    this.closeBtn = this.overlay.querySelector('[data-search-close]');
    this.input = this.overlay.querySelector('[data-search-input]');
    this.results = this.overlay.querySelector('[data-search-results]');

    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => this.toggle());
    });

    this.closeBtn?.addEventListener('click', () => this.close());

    this.input?.addEventListener('input', debounce((e) => {
      this.search(e.target.value);
    }, 300));

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  toggle() {
    if (this.overlay.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.overlay.classList.add('is-open');
    this.input?.focus();
  }

  close() {
    this.overlay.classList.remove('is-open');
    if (this.input) this.input.value = '';
    if (this.results) this.results.innerHTML = '';
  }

  async search(query) {
    if (query.length < 2) {
      this.results.innerHTML = '';
      return;
    }

    try {
      const response = await fetch(
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product&resources[limit]=4`
      );

      if (!response.ok) throw new Error('Search failed');

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const resultsMarkup = html.querySelector('#shopify-section-predictive-search')?.innerHTML;

      if (resultsMarkup) {
        this.results.innerHTML = resultsMarkup;
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  }
}

// ============================================================================
// Cart - Always redirect to /cart page (no drawer)
// ============================================================================

class Cart {
  constructor() {
    this.toggleBtns = document.querySelectorAll('[data-cart-toggle]');
    this.bindEvents();
  }

  bindEvents() {
    // Cart icon clicks - go to cart page
    this.toggleBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = '/cart';
      });
    });

    // Listen for add to cart events - redirect to cart
    document.addEventListener('cart:add', () => {
      window.location.href = '/cart';
    });

    // Cart page quantity buttons
    document.addEventListener('click', (e) => {
      const minusBtn = e.target.closest('[data-cart-qty-minus]');
      const plusBtn = e.target.closest('[data-cart-qty-plus]');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (minusBtn) {
        e.preventDefault();
        const key = minusBtn.dataset.cartQtyMinus;
        const input = document.querySelector(`[data-cart-qty-input="${key}"]`);
        if (input) {
          const newQty = Math.max(0, parseInt(input.value) - 1);
          this.updateItem(key, newQty);
        }
      }

      if (plusBtn) {
        e.preventDefault();
        const key = plusBtn.dataset.cartQtyPlus;
        const input = document.querySelector(`[data-cart-qty-input="${key}"]`);
        if (input) {
          const newQty = parseInt(input.value) + 1;
          this.updateItem(key, newQty);
        }
      }

      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.cartRemove;
        this.updateItem(key, 0);
      }
    });
  }

  async updateItem(key, quantity) {
    try {
      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) throw new Error('Update failed');

      // Reload page to show updated cart
      window.location.reload();
    } catch (error) {
      console.error('Cart update error:', error);
    }
  }
}

// ============================================================================
// Add to Cart
// ============================================================================

class AddToCart {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;

      e.preventDefault();
      const variantId = btn.dataset.addToCart;

      btn.disabled = true;
      btn.classList.add('is-loading');

      try {
        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }]
          })
        });

        if (!response.ok) throw new Error('Add to cart failed');

        document.dispatchEvent(new CustomEvent('cart:add'));
      } catch (error) {
        console.error('Add to cart error:', error);
      } finally {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    });
  }
}

// ============================================================================
// Sticky Header
// ============================================================================

class StickyHeader {
  constructor() {
    this.header = document.querySelector('[data-header]');
    if (!this.header || !this.header.classList.contains('header--sticky')) return;

    this.lastScroll = 0;
    this.bindEvents();
  }

  bindEvents() {
    window.addEventListener('scroll', debounce(() => {
      const currentScroll = window.scrollY;

      if (currentScroll > 100) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }

      this.lastScroll = currentScroll;
    }, 10));
  }
}

// ============================================================================
// Announcement Bar Slider
// ============================================================================

class AnnouncementSlider {
  constructor() {
    this.slider = document.querySelector('[data-slider]');
    if (!this.slider) return;

    this.slides = this.slider.querySelectorAll('.announcement-bar__slide');
    if (this.slides.length <= 1) return;

    this.currentIndex = 0;
    this.interval = null;

    this.init();
  }

  init() {
    // Hide all slides except first
    this.slides.forEach((slide, index) => {
      if (index !== 0) slide.style.display = 'none';
    });

    // Start auto rotation
    this.startRotation();

    // Bind navigation buttons
    const prevBtn = document.querySelector('.announcement-bar__prev');
    const nextBtn = document.querySelector('.announcement-bar__next');

    prevBtn?.addEventListener('click', () => {
      this.prev();
      this.restartRotation();
    });

    nextBtn?.addEventListener('click', () => {
      this.next();
      this.restartRotation();
    });
  }

  startRotation() {
    this.interval = setInterval(() => this.next(), 5000);
  }

  restartRotation() {
    clearInterval(this.interval);
    this.startRotation();
  }

  next() {
    this.slides[this.currentIndex].style.display = 'none';
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.slides[this.currentIndex].style.display = 'flex';
  }

  prev() {
    this.slides[this.currentIndex].style.display = 'none';
    this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.slides[this.currentIndex].style.display = 'flex';
  }
}

// ============================================================================
// Notification System
// ============================================================================

class Notification {
  constructor() {
    this.container = this.createContainer();
  }

  createContainer() {
    let container = document.querySelector('.notification');
    if (!container) {
      container = document.createElement('div');
      container.className = 'notification';
      document.body.appendChild(container);
    }
    return container;
  }

  show(message, type = 'default', duration = 3000) {
    this.container.textContent = message;
    this.container.className = 'notification';

    if (type === 'success') {
      this.container.classList.add('notification--success');
    } else if (type === 'error') {
      this.container.classList.add('notification--error');
    }

    // Force reflow for animation
    this.container.offsetHeight;
    this.container.classList.add('is-visible');

    setTimeout(() => {
      this.container.classList.remove('is-visible');
    }, duration);
  }
}

// Global notification instance
window.notification = new Notification();

// ============================================================================
// Product Gallery
// ============================================================================

class ProductGallery {
  constructor() {
    this.gallery = document.querySelector('.product-gallery');
    if (!this.gallery) return;

    this.mainImage = this.gallery.querySelector('[data-product-featured-image]');
    // Support both attribute formats
    this.thumbs = this.gallery.querySelectorAll('[data-product-thumb], [data-media-id]');

    if (!this.mainImage || !this.thumbs.length) return;

    this.bindEvents();
  }

  bindEvents() {
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.changeImage(thumb);
      });
    });
  }

  changeImage(thumb) {
    // Support both attribute formats
    const newSrc = thumb.dataset.mediaSrc || thumb.dataset.mediaUrl;
    if (!newSrc) return;

    // Update main image
    this.mainImage.src = newSrc;

    // Update active state
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }
}

// ============================================================================
// Image Lazy Loading Enhancement
// ============================================================================

class LazyImages {
  constructor() {
    this.images = document.querySelectorAll('img[loading="lazy"]');
    if (!this.images.length) return;

    if ('IntersectionObserver' in window) {
      this.initObserver();
    }
  }

  initObserver() {
    const options = {
      root: null,
      rootMargin: '50px 0px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.classList.add('is-loaded');
          observer.unobserve(img);
        }
      });
    }, options);

    this.images.forEach(img => observer.observe(img));
  }
}

// ============================================================================
// Smooth Scroll
// ============================================================================

class SmoothScroll {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;

      const targetId = anchor.getAttribute('href');
      if (targetId === '#') return;

      const target = document.querySelector(targetId);
      if (!target) return;

      e.preventDefault();
      const headerHeight = document.querySelector('[data-header]')?.offsetHeight || 0;
      const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    });
  }
}

// ============================================================================
// Desktop Menu
// ============================================================================

class DesktopMenu {
  constructor() {
    this.menuItems = document.querySelectorAll('.header__menu-item.has-dropdown');
    this.bindEvents();
  }

  bindEvents() {
    this.menuItems.forEach(item => {
      const link = item.querySelector('.header__menu-link');
      if (link) {
        link.addEventListener('click', (e) => {
          // Prevent standard navigation on links that have dropdowns
          e.preventDefault();

          const isOpen = item.classList.contains('is-open');

          // Close other open menus
          this.menuItems.forEach(i => i.classList.remove('is-open'));

          if (!isOpen) {
            item.classList.add('is-open');
          }
        });
      }
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.header__menu-item.has-dropdown')) {
        this.menuItems.forEach(i => i.classList.remove('is-open'));
      }
    });
  }
}

// ============================================================================
// Initialize
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
  new MobileMenu();
  new Search();
  new Cart();
  new AddToCart();
  new StickyHeader();
  new AnnouncementSlider();
  new ProductGallery();
  new LazyImages();
  new SmoothScroll();
  new DesktopMenu();
});

// Listen for successful cart add to show notification
document.addEventListener('cart:add', () => {
  if (window.notification) {
    window.notification.show('Product added to cart', 'success');
  }
});